import { HeaderBar } from "@/features/common/components/HeaderBar";
import { useChangePassword } from "@/features/settings/api/useChangePassword";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { Limit } from "@/lib/common/Limit";
import { useFocusOnLoad } from "@/lib/common/useFocus";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";
import { useTitle } from "@/lib/common/useTitle";
import { Form } from "@/lib/form/components/Form";
import { useAppForm } from "@/lib/form/useAppForm";
import { useAuthState } from "@/lib/state";
import { useCallback, useEffect, useMemo, type FC } from "react";
import z from "zod";

export const PageChangePassword: FC = () => {
	useTitle("Change Password");

	const ref = useFocusOnLoad();
	const changePassword = useChangePassword();
	useNotifyMutationSuccess(changePassword, "Password Changed");
	const { dispatchAuthLoggedOut } = useAuthState();

	const validators = useMemo(() => z.object({
		currentPassword: z.string().min(8, "Your password must be at least 8 characters"),
		newPassword: z.string().min(8, "Your new password must be at least 8 characters"),
		confirmNewPassword: z.string()
	}).refine(x => x.newPassword === x.confirmNewPassword, {
		message: "Passwords must match",
		path: ["confirmNewPassword"]
	}), []);

	const form = useAppForm({
		defaultValues: {
			currentPassword: "",
			newPassword: "",
			confirmNewPassword: ""
		},
		validators: {
			onMount: validators,
			onChange: validators
		},
		onSubmit: async ({ value }) => {
			await changePassword.mutateAsync({
				currentPassword: value.currentPassword,
				newPassword: value.newPassword
			});
		}
	});

	useEffect(() => {
		if (!changePassword.isSuccess) {
			return;
		}

		dispatchAuthLoggedOut();
	}, [changePassword.isSuccess]);

	const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		form.handleSubmit();
	}, [form]);

	const breadcrumb = useMemo(() => [
		{ label: "Settings" },
		{ label: "Change Password" }
	], []);

	return (
		<>
			<HeaderBar title="Change Password" description="Please note that you will be logged out after changing your password." breadcrumb={breadcrumb} />

			{
				!changePassword.isSuccess &&
				<Limit border={true}>
					<Form onSubmit={onSubmit} className="p-3">
						<form.AppField name="currentPassword">
							{
								field => <field.TextField label="Current Password" type="password" ref={ref} required />
							}
						</form.AppField>
						<form.AppField name="newPassword">
							{
								field => <field.TextField label="New Password" type="password" required />
							}
						</form.AppField>
						<form.AppField name="confirmNewPassword">
							{
								field => <field.TextField label="Confirm New Password" type="password" required />
							}
						</form.AppField>
						<ErrorDisplay error={changePassword.error} />

						<form.AppForm>
							<form.SubmitButton label="Confirm" />
						</form.AppForm>
					</Form>
				</Limit>
			}
		</>
	);
};
